import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchRejectionReasons,
  CreateRejectionReason,
  UpdateRejectionReason,
  DeleteRejectionReason,
  FetchRejectionReasonsToDecline,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import { RejectionReason } from 'src/interfaces/rejectionReason'

function* onFetchRejectionReasons(action: AsyncAction<void, Array<RejectionReason>>) {
  try {
    const { payload }: { payload: { data: Array<RejectionReason> } } = yield call(
      callSecureApi,
      Endpoints.FetchRejectionReasons,
      {
        apiService: ApiService.Paraworks,
      },
    )

    const reasons = payload.data

    yield put(FetchRejectionReasons.success(reasons))

    if (action.next) {
      action.next(null, reasons)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch rejection reasons'

    yield put(FetchRejectionReasons.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchRejectionReasonsToDecline(action: AsyncAction<void, Array<RejectionReason>>) {
  try {
    const { payload }: { payload: { data: Array<RejectionReason> } } = yield call(
      callSecureApi,
      Endpoints.FetchRejectionReasonsToDecline,
      {
        apiService: ApiService.Paraworks,
      },
    )

    const reasons = payload.data

    yield put(FetchRejectionReasonsToDecline.success(reasons))

    if (action.next) {
      action.next(null, reasons)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch rejection reasons'

    yield put(FetchRejectionReasonsToDecline.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CreateRejectionReasonPayload {
  name: string
  message: string
}

function* onCreateRejectionReason(action: AsyncAction<CreateRejectionReasonPayload, void>) {
  try {
    const { name, message } = action.payload

    yield call(callSecureApi, Endpoints.CreateRejectionReason, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      body: {
        name,
        message,
      },
    })

    yield put(CreateRejectionReason.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create rejection reason'

    yield put(CreateRejectionReason.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateRejectionReasonPayload {
  name: string
  message: string
  reasonId: string
}

function* onUpdateRejectionReason(action: AsyncAction<UpdateRejectionReasonPayload, void>) {
  try {
    const { name, message, reasonId } = action.payload

    yield call(callSecureApi, Endpoints.UpdateRejectionReason, {
      apiService: ApiService.Paraworks,
      method: 'PUT',
      body: {
        name,
        message,
      },
      params: [
        {
          field: ':reasonId',
          value: reasonId,
        },
      ],
    })

    yield put(UpdateRejectionReason.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update rejection reason'

    yield put(UpdateRejectionReason.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeleteRejectionReasonPayload {
  reasonId: string
}

function* onDeleteRejectionReason(action: AsyncAction<DeleteRejectionReasonPayload, void>) {
  try {
    const { reasonId } = action.payload

    yield call(callSecureApi, Endpoints.DeleteRejectionReason, {
      apiService: ApiService.Paraworks,
      method: 'DELETE',

      params: [
        {
          field: ':reasonId',
          value: reasonId,
        },
      ],
    })

    yield put(DeleteRejectionReason.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete rejection reason'

    yield put(DeleteRejectionReason.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* rejectionReasonsSaga() {
  yield takeEvery(FetchRejectionReasons.type.REQUEST, onFetchRejectionReasons)
  yield takeEvery(FetchRejectionReasonsToDecline.type.REQUEST, onFetchRejectionReasonsToDecline)
  yield takeEvery(CreateRejectionReason.type.REQUEST, onCreateRejectionReason)
  yield takeEvery(UpdateRejectionReason.type.REQUEST, onUpdateRejectionReason)
  yield takeEvery(DeleteRejectionReason.type.REQUEST, onDeleteRejectionReason)
}
